import { sql } from "drizzle-orm";
import { check, index, integer, pgTable, text, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { organisations, schoolPupils, students, users } from "./schemaBase.js";

export type FamilyLinkDomain = "private" | "school";
export type FamilyLinkStatus = "pending" | "linked" | "declined" | "cancelled";
export interface LinkedFamilyChild {
  id: string;
  fullName: string;
  domain: FamilyLinkDomain;
  schoolName: string | null;
  pupilId: string | null;
}
export interface FamilyLinkRequestView {
  id: string;
  childName: string;
  parentName: string;
  domain: FamilyLinkDomain;
  schoolName: string | null;
  note: string;
  status: FamilyLinkStatus;
  verificationRequested: boolean;
  createdAt: string;
  decidedAt: string | null;
  decisionReason: string | null;
}
export interface FamilyLinkData {
  enabled: boolean;
  learners: LinkedFamilyChild[];
  requests: FamilyLinkRequestView[];
}
export interface StaffFamilyLinkRequest extends FamilyLinkRequestView {
  requesterEmail: string;
  accountEmail: string | null;
  accountEmailConfirmed: boolean;
  candidate: LinkedFamilyChild | null;
  verificationDelivery: string | null;
}

/** Requests never create a learner, change a payer, or grant access on submission. */
export const familyLinkRequests = pgTable("family_link_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organisationId: varchar("organisation_id").notNull().references(() => organisations.id),
  requestedByUserId: varchar("requested_by_user_id").notNull().references(() => users.id),
  requesterEmail: text("requester_email").notNull(),
  requestKey: text("request_key").notNull(),
  childName: text("child_name").notNull(),
  parentName: text("parent_name").notNull(),
  domain: text("domain").$type<FamilyLinkDomain>().notNull(),
  schoolName: text("school_name"),
  note: text("note").notNull().default(""),
  status: text("status").$type<FamilyLinkStatus>().notNull().default("pending"),
  contactEmail: text("contact_email"),
  targetStudentId: varchar("target_student_id").references(() => students.id),
  targetPupilId: varchar("target_pupil_id").references(() => schoolPupils.id),
  proofEmail: text("proof_email"),
  proofTokenHash: text("proof_token_hash"),
  proofExpiresAt: timestamp("proof_expires_at", { withTimezone: true }),
  proofAttempts: integer("proof_attempts").notNull().default(0),
  proofLastSentAt: timestamp("proof_last_sent_at", { withTimezone: true }),
  verificationDelivery: text("verification_delivery"),
  verificationMethod: text("verification_method"),
  decidedByUserId: varchar("decided_by_user_id").references(() => users.id),
  decidedAt: timestamp("decided_at", { withTimezone: true }),
  decisionReason: text("decision_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, table => [
  uniqueIndex("family_link_requests_pending_identity").on(table.organisationId, table.requestedByUserId, table.requestKey)
    .where(sql`${table.status} = 'pending'`),
  uniqueIndex("family_link_requests_proof_token").on(table.proofTokenHash),
  index("family_link_requests_review").on(table.organisationId, table.status, table.createdAt),
  index("family_link_requests_requester").on(table.organisationId, table.requestedByUserId, table.createdAt),
  check("family_link_requests_domain", sql`${table.domain} in ('private', 'school')`),
  check("family_link_requests_status", sql`${table.status} in ('pending', 'linked', 'declined', 'cancelled')`),
]);
